import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const ALLOWED_HOSTS = new Set([
  "www.payfast.co.za",
  "payfast.co.za",
  "sandbox.payfast.co.za",
]);

function safeParseUrl(u) {
  try {
    // If already encoded, decode once (but don’t keep decoding repeatedly).
    const raw = String(u || "").trim();
    if (!raw) return null;

    // Handle cases where u is encoded in the query string (common)
    const maybeDecoded = raw.includes("%") ? decodeURIComponent(raw) : raw;

    const url = new URL(maybeDecoded);
    return url;
  } catch {
    return null;
  }
}

export default function PayRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  const [msg, setMsg] = useState("Redirecting to payment…");
  const formRef = useRef(null);

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Backward-compatible GET redirect (u=)
  const u = qs.get("u") || "";

  // Optional: POST mode (recommended). If you later change your navigation to pass these, it will work.
  // - pf: payfast endpoint (https://www.payfast.co.za/eng/process or sandbox)
  // - f : base64url-encoded JSON string of fields (merchant_id, merchant_key, ... signature, etc.)
  const pfEndpoint = qs.get("pf") || "";
  const fieldsB64 = qs.get("f") || "";

  const postMode = !!pfEndpoint && !!fieldsB64;

  useEffect(() => {
    // 1) POST mode (most reliable; avoids query-string length + encoding issues)
    if (postMode) {
      const endpointUrl = safeParseUrl(pfEndpoint);

      if (!endpointUrl || !ALLOWED_HOSTS.has(endpointUrl.hostname)) {
        setMsg("Blocked unsafe redirect destination.");
        navigate("/", { replace: true });
        return;
      }

      let fields = null;
      try {
        // base64url -> base64
        const b64 = fieldsB64.replace(/-/g, "+").replace(/_/g, "/");
        const json = atob(b64);
        fields = JSON.parse(json);
      } catch {
        setMsg("Invalid payment payload.");
        navigate("/", { replace: true });
        return;
      }

      // Build hidden form inputs then auto-submit
      setMsg("Redirecting to payment…");
      const t = setTimeout(() => {
        try {
          if (formRef.current) formRef.current.submit();
        } catch {
          // If submit fails, fall back to GET redirect if present
          if (u) window.location.replace(u);
        }
      }, 50);

      return () => clearTimeout(t);
    }

    // 2) GET mode (existing behavior) with strict allowlist
    if (!u) {
      navigate("/", { replace: true });
      return;
    }

    const url = safeParseUrl(u);
    if (!url || !ALLOWED_HOSTS.has(url.hostname)) {
      setMsg("Blocked unsafe redirect destination.");
      navigate("/", { replace: true });
      return;
    }

    // PayFast expects /eng/process typically; allow any path under allowed hosts
    setMsg("Redirecting to payment…");
    window.location.replace(url.toString());
  }, [postMode, pfEndpoint, fieldsB64, u, navigate]);

  // Render the POST form only when needed
  const postForm = useMemo(() => {
    if (!postMode) return null;

    const endpointUrl = safeParseUrl(pfEndpoint);
    if (!endpointUrl || !ALLOWED_HOSTS.has(endpointUrl.hostname)) return null;

    let fields = null;
    try {
      const b64 = fieldsB64.replace(/-/g, "+").replace(/_/g, "/");
      const json = atob(b64);
      fields = JSON.parse(json);
    } catch {
      return null;
    }

    // Only include primitive values (string/number) to avoid weirdness
    const entries = Object.entries(fields || {}).filter(
      ([, v]) => v != null && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    );

    return (
      <form ref={formRef} method="POST" action={endpointUrl.toString()}>
        {entries.map(([k, v]) => (
          <input key={k} type="hidden" name={k} value={String(v)} />
        ))}
      </form>
    );
  }, [postMode, pfEndpoint, fieldsB64]);

  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{msg}</div>
      <div style={{ opacity: 0.8 }}>
        If nothing happens, please go back and try again.
      </div>
      {postForm}
    </div>
  );
}